<template>
  <Layout>
    <PageHeader :title="$t('module.certificate')" :items="breadcrumbs" />
    <b-alert v-if="!canRead" show variant="warning">{{
      $t("message.noPermissionMessage")
    }}</b-alert>
    <div v-else class="card">
      <div class="card-body">
        <div v-if="canWrite" class="d-flex justify-content-end mb-3">
          <b-button
            variant="info"
            :disabled="busy || !canWrite"
            @click="openForm()"
            ><i class="mdi mdi-plus mr-1"></i
            >{{ $t("label.createX", [$t("module.certificate")]) }}</b-button
          >
        </div>
        <div v-if="loading" class="text-center p-5">
          <b-spinner variant="info" />
        </div>
        <b-alert v-else-if="error" show variant="danger"
          >{{ $t("label.certificateLoadError") }}
          <b-button
            size="sm"
            variant="outline-danger"
            @click="loadCertificates()"
            >{{ $t("label.retryPermissionDefinitions") }}</b-button
          ></b-alert
        >
        <template v-else>
          <b-table
            :items="rows"
            :fields="fields"
            primary-key="uuid"
            responsive
            show-empty
          >
            <template #cell(templatePath)="{ item }"
              ><img
                v-if="item.templatePath"
                :src="item.templatePath"
                :alt="item.name"
                class="certificate-thumbnail rounded border"
              /><span v-else>-</span></template
            >
            <template #cell(isActive)="{ item }"
              ><b-form-checkbox
                :key="`${item.uuid}-${toggleVersion}`"
                :checked="item.isActive"
                switch
                :disabled="busy || !canWrite"
                @change="confirmStatus(item, $event)"
                >{{
                  $t(item.isActive ? "label.active" : "label.inactive")
                }}</b-form-checkbox
              ></template
            >
            <template #cell(actions)="{ item }"
              ><b-button
                variant="link"
                class="p-0"
                :disabled="busy"
                :title="
                  $t(canWrite ? 'label.updateX' : 'label.viewX', [
                    $t('module.certificate'),
                  ])
                "
                :aria-label="
                  $t(canWrite ? 'label.updateX' : 'label.viewX', [
                    $t('module.certificate'),
                  ])
                "
                @click="openForm(item)"
                ><i
                  class="mdi font-size-18"
                  :class="canWrite ? 'mdi-pencil-outline' : 'mdi-eye-outline'"
                ></i></b-button
              ><b-button
                variant="link"
                class="p-0 ml-2"
                :disabled="busy"
                :title="$t('label.certificatePdfPreview')"
                :aria-label="$t('label.certificatePdfPreview')"
                @click="openPreview(item)"
                ><i class="mdi mdi-file-pdf-outline font-size-18"></i></b-button
            ></template>
          </b-table>
          <div
            v-if="total"
            class="d-flex flex-wrap justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", total, [total])
            }}</small>
            <b-pagination
              :value="page"
              :total-rows="total"
              :per-page="pageSize"
              class="mb-0"
              @change="changePage"
            />
          </div>
        </template>
      </div>
    </div>
    <b-modal
      id="certificate-form"
      :title="
        $t(
          selectedUUID
            ? canWrite
              ? 'label.updateX'
              : 'label.viewX'
            : 'label.createX',
          [$t('module.certificate')],
        )
      "
      size="xl"
      hide-footer
      centered
      :no-close-on-backdrop="formSaving"
      :no-close-on-esc="formSaving"
      :hide-header-close="formSaving"
      @hidden="formVisible = false"
    >
      <CertificateForm
        v-if="formVisible && canRead"
        :key="selectedUUID || 'new'"
        :certificate-u-u-i-d="selectedUUID"
        :read-only="!canWrite"
        @done="closeForm"
        @saved="saved"
        @saving="formSaving = $event"
      />
    </b-modal>
    <b-modal
      id="certificate-pdf-preview"
      :title="$t('label.certificatePdfPreview')"
      size="xl"
      hide-footer
      centered
      @hidden="previewVisible = false"
    >
      <CertificatePdfPreview
        v-if="previewVisible && canRead"
        :key="previewUUID"
        :certificate-u-u-i-d="previewUUID"
        @done="$bvModal.hide('certificate-pdf-preview')"
      />
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import CertificateForm from "./form.vue";
import CertificatePdfPreview from "./preview.vue";
import { certificateService } from "@/services/api/certificate.service";
import { certificateModel } from "@/models/api/certificate.model";
import { alertHelper, sharedHelper, sessionHelper } from "@/utils";
export default {
  name: "CertificateConfiguration",
  page: { title: i18n.t("module.certificate") },
  components: { Layout, PageHeader, CertificateForm, CertificatePdfPreview },
  data() {
    return {
      rows: [],
      loading: true,
      error: false,
      busy: false,
      page: 1,
      pageSize: 15,
      total: 0,
      requestId: 0,
      toggleVersion: 0,
      selectedUUID: "",
      formVisible: false,
      formSaving: false,
      previewVisible: false,
      previewUUID: "",
    };
  },
  computed: {
    permission() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .cert_admin || 0
      );
    },
    canRead() {
      return this.permission >= 1;
    },
    canWrite() {
      return this.permission === 2;
    },
    breadcrumbs() {
      return [
        { text: this.$t("module.orientation"), href: "/orientations" },
        { text: this.$t("module.certificate"), active: true },
      ];
    },
    fields() {
      return [
        { key: "templatePath", label: this.$t("label.certificateBackground") },
        { key: "name", label: this.$t("label.name") },
        { key: "isActive", label: this.$t("label.active") },
        { key: "actions", label: this.$t("label.action") },
      ];
    },
  },
  watch: {
    permission(level) {
      if (level < 1) {
        this.closeForm();
        this.$bvModal.hide("certificate-pdf-preview");
        this.formVisible = false;
        this.previewVisible = false;
      } else if (level < 2 && !this.selectedUUID) this.closeForm();
      this.loadCertificates();
    },
  },
  created() {
    this.loadCertificates();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    openPreview(item) {
      if (
        this.busy ||
        !item.uuid ||
        sessionHelper.getPermissionLevel("cert_admin") < 1
      )
        return;
      this.previewUUID = item.uuid;
      this.previewVisible = true;
      this.$nextTick(() => this.$bvModal.show("certificate-pdf-preview"));
    },
    async loadCertificates() {
      const request = ++this.requestId;
      if (sessionHelper.getPermissionLevel("cert_admin") < 1) {
        this.rows = [];
        this.total = 0;
        this.page = 1;
        this.loading = false;
        this.error = false;
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        const result = await certificateService.getAll(
          this.page,
          this.pageSize,
        );
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid certificate list.");
        if (request !== this.requestId) return;
        this.rows = result.data.map(certificateModel);
        this.total = result.pagination?.total ?? result.data.length;
        this.page = result.pagination?.current_page || this.page;
        this.pageSize = result.pagination?.per_page || this.pageSize;
      } catch (error) {
        if (request === this.requestId) this.error = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    changePage(page) {
      this.page = page;
      return this.loadCertificates();
    },
    openForm(item) {
      if (
        this.busy ||
        sessionHelper.getPermissionLevel("cert_admin") < (item?.uuid ? 1 : 2)
      )
        return;
      this.selectedUUID = item?.uuid || "";
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("certificate-form"));
    },
    closeForm() {
      this.$bvModal.hide("certificate-form");
    },
    saved() {
      this.closeForm();
      this.loadCertificates();
    },
    async confirmStatus(item, isActive) {
      if (this.busy || sessionHelper.getPermissionLevel("cert_admin") !== 2) {
        this.toggleVersion++;
        return;
      }
      this.busy = true;
      try {
        const action = this.$t(
          isActive ? "label.activate" : "label.deactivate",
        );
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("label.confirmChangeStatusX", [action, item.name]),
          {
            title: this.$t("label.confirmStatusChange"),
            okTitle: this.$t("label.confirm"),
            cancelTitle: this.$t("label.cancel"),
            okVariant: "info",
            centered: true,
          },
        );
        if (!confirmed || sessionHelper.getPermissionLevel("cert_admin") !== 2)
          return;
        const result = await certificateService.updateStatus(
          item.uuid,
          isActive,
        );
        if (!result?.success)
          throw new Error(result?.message || this.$t("message.unknownError"));
        alertHelper.alertSuccess({
          message: this.$t("message.updateStatusSuccess"),
        });
        await this.loadCertificates();
      } catch (error) {
        alertHelper.alertError({
          message:
            sharedHelper.getErrorMessage(error) ||
            this.$t("message.unknownError"),
          title: this.$t("message.error"),
        });
      } finally {
        this.toggleVersion++;
        this.busy = false;
      }
    },
  },
};
</script>
<style scoped>
.certificate-thumbnail {
  width: 60px;
  height: 42px;
  object-fit: contain;
}
</style>
